import React, { memo, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import { Platform, Pressable, View, ViewProps, FlatList, Text, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { CLEAR_PAGE_BADGES, SECOND_MENU_CONTEXT, SECOND_MENU_TOGGLE, HIDE_MESSAGE_MODAL, TOGGLE_EASY_MODE, CURRENT_PAGE, SKIP_ONBOARDING } from '@reduxLocal/actions/types';
import styles from '@stylesheet';
import LottieView from '@utils/lottieAnimation';
import { useTranslation } from 'react-i18next';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useNavigation } from '@react-navigation/native';
import { Alert, Badge } from '@mui/material';
import { YStack } from 'tamagui';
import { CountUp } from 'use-count-up';
import { cancelAllRequests } from '@api/api';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialIcons } from '@expo/vector-icons';
import webTransition from '@utils/webTransition';
import { LinearGradient } from 'expo-linear-gradient';
import * as Animatable from 'react-native-animatable';
import { useAlertDialog } from '@components/Global/AlertDialog/useAlertDialog';
import { moderateScale as ms } from '@utils/scale';

type PrimaryMenuTypes = {
  item: { 
    key: string, 
    screen: string, 
    mobileIcon: JSX.Element, 
    header: string, 
    icon: any, 
    function: Function, 
    secondaryMenu: { 
      active: boolean, 
      content: any[] 
    }, 
    stack?: string, 
    disabled?: boolean,
    new?: boolean // Added new prop for new feature indication
  };
  style?: ViewProps;
  tint?: boolean;
  noBackColor?: boolean;
};

type SecondaryMenuItemTypes = {
  subItem: {
    key: string;
    screen: string;
    header: string;
    function?: Function;
    new?: boolean;
  };
  index: number;
  parentScreen: string;
  isSecondaryActive: boolean;
  theme: boolean;
  _handleNavigate: (screen: string, stack: string) => void;
};

// Separate component for secondary menu items to avoid hook violations
const SecondaryMenuItem = memo(({ subItem, index, parentScreen, isSecondaryActive, theme, _handleNavigate }: SecondaryMenuItemTypes) => {
  const { t } = useTranslation();
  const [subHovered, setSubHovered] = useState(false);
  const subHoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const page = useSelector((state: DefaultRootState) => state.navigation.page);
  const easyMode = useSelector((state: DefaultRootState) => state.UI.easyMode);
  const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);
  const { showConfirm } = useAlertDialog();
  const dispatchSecondary = useDispatch();

  const handleSubHoverIn = useCallback(() => {
    if (subHoverTimeoutRef.current) {
      clearTimeout(subHoverTimeoutRef.current);
    }
    subHoverTimeoutRef.current = setTimeout(() => {
      setSubHovered(true);
    }, 50);
  }, []);

  const handleSubHoverOut = useCallback(() => {
    if (subHoverTimeoutRef.current) {
      clearTimeout(subHoverTimeoutRef.current);
    }
    subHoverTimeoutRef.current = setTimeout(() => {
      setSubHovered(false);
    }, 100);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (subHoverTimeoutRef.current) {
        clearTimeout(subHoverTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Animatable.View
      key={subItem.key}
      animation="slideInLeft"
      delay={index * 50}
      duration={300}
    >
      <Pressable
        onHoverIn={handleSubHoverIn}
        onHoverOut={handleSubHoverOut}
        onPress={async () => {
          // Check onboarding before executing function or navigating
          if (onboardingInProgress) {
            const result = await showConfirm(
              'Skip Onboarding?',
              `You are currently in the onboarding process. This action will skip the onboarding. Do you want to continue?`,
              {
                cancelText: 'Cancel',
                confirmText: 'Skip & Continue'
              }
            );
            
            if (result !== true) {
              return;
            }
            
            dispatchSecondary({ type: SKIP_ONBOARDING });
          }

          if (subItem.function) {
            subItem.function();
          } else {
            _handleNavigate(subItem.screen, parentScreen);
          }
        }}
        style={[
          {
            flexDirection: 'row',
            alignItems: 'center',
            paddingHorizontal: ms(16),
            paddingVertical: ms(10),
            marginVertical: ms(2),
            borderRadius: ms(10),
            minHeight: ms(40),
            maxWidth: "95%",
            position: 'relative',
            backgroundColor: isSecondaryActive
              ? theme
                ? 'rgba(102, 126, 234, 0.12)'
                : 'rgba(74, 144, 226, 0.12)'
              : 'transparent',
            borderLeftWidth: isSecondaryActive ? ms(3) : ms(3),
            borderLeftColor: isSecondaryActive
              ? theme ? '#667eea' : '#4a90e2'
              : 'transparent',
            ...webTransition('all', '200ms', 'ease-out'),
            // Add glow effect for new secondary items
            ...(subItem.new && {
              shadowColor: '#FF4444',
              shadowOpacity: 0.2,
              shadowRadius: ms(4),
              elevation: 2,
            })
          },
          subHovered && {
            backgroundColor: theme
              ? 'rgba(102, 126, 234, 0.08)'
              : 'rgba(74, 144, 226, 0.08)',
            transform: [{ translateX: ms(4) }],
            shadowColor: theme ? '#667eea' : '#4a90e2',
            shadowOffset: { width: 0, height: ms(2) },
            shadowOpacity: 0.1,
            shadowRadius: ms(4),
            elevation: 2,
          }
        ]}
      >
        <LinearGradient
          colors={(() => {
            const colorPairs: [string, string][] = [
              ['#FF5252', '#FF1744'],
              ['#FFB300', '#FFA000'],
              ['#29B6F6', '#0288D1'],
              ['#66BB6A', '#43A047'],
              ['#AB47BC', '#8E24AA'],
              ['#FFD600', '#FFC107'],
              ['#EC407A', '#E91E63'],
              ['#26A69A', '#00897B'],
              ['#7E57C2', '#673AB7'],
              ['#FF7043', '#FF5722']
            ];
            return colorPairs[index % 10];
          })()}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={{
            width: ms(10),
            height: ms(10),
            borderRadius: ms(5),
            marginRight: ms(14),
            shadowColor: [
              '#FF5252', '#FFB300', '#29B6F6', '#66BB6A', '#AB47BC',
              '#FFD600', '#EC407A', '#26A69A', '#7E57C2', '#FF7043'
            ][index % 10],
            shadowOffset: { width: 0, height: ms(1) },
            shadowOpacity: 0.4,
            shadowRadius: ms(2),
            elevation: 2,
          }}
        />
        <Text
          selectable={false}
          numberOfLines={1}
          style={{
            color: theme ? "#333333" : '#ffffff',
            fontWeight: isSecondaryActive ? '600' : '500',
            fontSize: ms(13),
            flex: 1,
            textShadowColor: isSecondaryActive
              ? (theme ? 'rgba(102, 126, 234, 0.2)' : 'rgba(255,255,255,0.2)')
              : 'transparent',
            textShadowOffset: { width: 0, height: ms(1) },
            textShadowRadius: ms(1),
          }}
        >
          {t(subItem.header)}
        </Text>
        {/* NEW feature indicator for secondary items */}
        {subItem.new && (
          <Animatable.View
            animation="pulse"
            iterationCount="infinite"
            duration={2000}
            style={{
              backgroundColor: '#FF4444',
              borderRadius: ms(8),
              paddingHorizontal: ms(5),
              paddingVertical: ms(1),
              marginRight: ms(8),
              minWidth: ms(18),
              alignItems: 'center',
              justifyContent: 'center',
              shadowColor: '#FF4444',
              shadowOffset: { width: 0, height: ms(1) },
              shadowOpacity: 0.4,
              shadowRadius: ms(2),
              elevation: 3,
              borderWidth: ms(0.5),
              borderColor: 'rgba(255, 255, 255, 0.2)',
            }}
          >
            <Text style={{
              color: 'white',
              fontSize: ms(8),
              fontWeight: 'bold',
              textAlign: 'center',
              textShadowColor: 'rgba(0,0,0,0.3)',
              textShadowOffset: { width: 0, height: ms(0.5) },
              textShadowRadius: ms(1),
            }}>
              NEW
            </Text>
          </Animatable.View>
        )}
        {isSecondaryActive && (
          <Animatable.View
            animation="pulse"
            iterationCount="infinite"
            duration={1500}
          >
            <MaterialIcons
              name="radio-button-checked"
              size={ms(12)}
              color={theme ? '#667eea' : '#4a90e2'}
            />
          </Animatable.View>
        )}
      </Pressable>
    </Animatable.View>
  );
});

const PrimaryMenu = ({ item, style, tint, noBackColor }: PrimaryMenuTypes) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);
  const animationRef = useRef<any>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const menuOpen = useSelector((state: DefaultRootState) => state.UI.menuOpen);
  const page = useSelector((state: DefaultRootState) => state.navigation.page);
  const dispatch = useDispatch();
  const markedStyle = styles.menuItem;
  const unMarkedStyle = styles.menuItemSelected;
  const hoveredStyle = styles.menuHovered;
  const navigation = useNavigation();
  const theme = themeSelector();
  const badges = useSelector((state: DefaultRootState) => state.UI?.badges?.[item.screen?.toLowerCase()]);
  const secondMenuArray = item.secondaryMenu?.content;
  const secondMenuActive = item.secondaryMenu?.active;
  const [menuExpand, setMenuExpand] = useState(false);
  const easyMode = useSelector((state: DefaultRootState) => state.UI.easyMode);
  const onboardingInProgress = useSelector((state: DefaultRootState) => state.UI.onboardingInProgress);
  const { showConfirm } = useAlertDialog();

  const collapseTrigger = useSelector((state: DefaultRootState) => state.UI?.secondaryMenuOpen);

  const confirmedScreen = (item) => {
    const isActive = page.name === (item.stack ? item.stack : item.screen);
    return isActive ? markedStyle : unMarkedStyle;
  };

  const isCurrentActive = page.name === (item.stack ? item.stack : item.screen);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // This effect runs ONLY when the global trigger from Redux changes.
  useEffect(() => {
    // We check if collapseTrigger is greater than 0 to avoid running on the initial render.
    if (collapseTrigger > 0) {
      setMenuExpand(false);
    }
  }, [collapseTrigger]); // Dependency array is crucial!

  // Debounced hover handlers to prevent rapid style changes
  const handleHoverIn = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (!isCurrentActive) {
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovered(true);
        animationRef.current?.reset();
        animationRef.current?.play();
      }, 50); // Small delay to prevent rapid changes
    }
  }, [isCurrentActive]);

  const handleHoverOut = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovered(false);
    }, 100); // Slightly longer delay for hover out
  }, []);

  const arrowStyle = {
    transform: [{ rotate: menuExpand ? '90deg' : '0deg' }],
    ...webTransition('transform', '250ms', 'ease-out')
  };

  const LottieIcon = useMemo(() => {
    const colorFilter = theme ? [{ keypath: "**", color: "#ffffff" }] : [{ keypath: "**", color: "#ffffff" }];
    return (
      <Animatable.View
        animation={isCurrentActive ? "pulse" : undefined}
        iterationCount="infinite"
        duration={2000}
        style={{
          transform: [{ scale: isCurrentActive ? 1.1 : 1 }],
          ...webTransition('transform', '200ms', 'ease-out')
        }}
      >
        <LottieView
          colorFilters={colorFilter}
          ref={animationRef} 
          style={[
            styles.menuItemIcon,
            {
              filter: isCurrentActive ? 'drop-shadow(0px 2px 4px rgba(255,255,255,0.3))' : 'none',
            }
          ]} 
          source={item.icon} 
          autoPlay 
        />
      </Animatable.View>
    );
  }, [item.icon, theme, isCurrentActive]);

  const _handleNavigate = useCallback(async (screen, stack) => {
   
    // Check onboarding before navigating
    if (onboardingInProgress) {
      const result = await showConfirm(
        'Skip Onboarding?',
        `You are currently in the onboarding process. Navigating to ${screen} will skip the onboarding. Do you want to continue?`,
        {
          cancelText: 'Cancel',
          confirmText: 'Skip & Navigate'
        }
      );
      
      if (result !== true) {
        return;
      }
      
      dispatch({ type: SKIP_ONBOARDING });
    }

    await cancelAllRequests();
    try {
      if (stack) {
      
        // Reset navigation to a specific screen within a nested stack
        navigation.reset({
          index: 0,
          routes: [
            {
              name: stack as never,
              state: {
                routes: [{ name: screen as never }],
              },
            } as never,
          ],
        });
      } else {
      
        // Reset navigation to a specific screen
        navigation.reset({
          index: 0,
          routes: [{ name: screen as never }] as never,
        });
      }
   
    } catch (e) {

    } 
  }, [navigation, onboardingInProgress, showConfirm, dispatch]);

  const handlePrimaryPress = async () => {
    // Check if onboarding is in progress - show warning dialog
    if (onboardingInProgress) {
      const result = await showConfirm(
        'Skip Onboarding?',
        'You are currently in the onboarding process. Navigating to another screen will skip the onboarding. Do you want to continue?',
        {
          cancelText: 'Cancel',
          confirmText: 'Skip & Navigate'
        }
      );
      
      if (result !== true) {
        return; // User cancelled or dialog was dismissed
      }
      
      // User confirmed - skip onboarding
      dispatch({ type: SKIP_ONBOARDING });
    }

    if (easyMode) {
      // Check if EasyModeScreen has exposed a close handler
      if (typeof window !== "undefined" && (window as any).__easyModeCloseHandler) {
        const shouldClose = await (window as any).__easyModeCloseHandler();
        if (!shouldClose) {
          return; // User cancelled or dialog was dismissed
        }
        // Handler already dispatched TOGGLE_EASY_MODE, so we don't need to dispatch again
        // Dispatch navigation item with no screen when easy mode is toggled off
        dispatch({ 
          type: CURRENT_PAGE, 
          payload: { route: { name: '', key: null, params: {} } } 
        });
      } else {
        // Fallback: use browser confirm if handler not available
        if (typeof window !== "undefined" && window.confirm) {
          const confirmed = window.confirm(
            "Disable easy mode and navigate to the screen?."
          );
          if (!confirmed) {
            return;
          }
          dispatch({ type: TOGGLE_EASY_MODE });
          // Dispatch navigation item with no screen when easy mode is toggled off
          dispatch({ 
            type: CURRENT_PAGE, 
            payload: { route: { name: '', key: null, params: {} } } 
          });
        } else {
          // fallback for non-browser envs or RN Alert
          dispatch({ type: TOGGLE_EASY_MODE });
          // Dispatch navigation item with no screen when easy mode is toggled off
          dispatch({ 
            type: CURRENT_PAGE, 
            payload: { route: { name: '', key: null, params: {} } } 
          });
        }
      }
    }
    try {
      dispatch({ type: CLEAR_PAGE_BADGES, payload: item.screen.toLowerCase() });
    } catch (e) {
      // Suppress error if page has no badge
    }

    const isCurrentScreen = page.name === (item.stack ? item.stack : item.screen);

    if (item.function) {
      item.function();
    }
    if (isCurrentScreen) {
      if (secondMenuActive) {
        setMenuExpand(true);
      }
    } else {
      dispatch({ type: SECOND_MENU_TOGGLE })
      if (secondMenuActive) {
        setTimeout(() => setMenuExpand(true), 50);
      }
      const stack = item.screen;
      // If there is a secondary menu, pick the first item's screen for navigation
      const firstSecondary = Array.isArray(secondMenuArray) && secondMenuArray.length > 0 ? secondMenuArray[0] : null;
      if (firstSecondary) {
        _handleNavigate(firstSecondary.screen, stack);
      } else {
        _handleNavigate(item.screen, stack);
      }
      // _handleNavigate(item.screen, stack);
    }
  };

  // Check if any secondary items have new property
  const hasNewSecondaryItems = useMemo(() => {
    if (!secondMenuArray || !Array.isArray(secondMenuArray)) return false;
    return secondMenuArray.some(subItem => subItem.new === true);
  }, [secondMenuArray]);

  // Check if this primary item or any of its secondary items are new
  const showNewIndicator = useMemo(() => {
    return item.new === true || hasNewSecondaryItems;
  }, [item.new, hasNewSecondaryItems]);

  const textStyle = {
    flex: 1,
    color: theme ? "#333333" : 'white',
    opacity: menuOpen ? 1 : 0,
    paddingHorizontal: ms(8),
    fontWeight: '600' as const,
    fontSize: ms(14),
    lineHeight: ms(18),
    ...webTransition('opacity', '150ms')
  };

  const secondaryListStyle = {
    maxHeight: menuExpand ? ms(400) : 0, // Reduced from 500 for better proportion
    opacity: menuExpand ? 1 : 0,
    overflow: 'hidden' as const,
    ...webTransition('max-height, opacity', '250ms', 'ease-in-out')
  };

  return (
    <View style={{ 
      paddingLeft: menuOpen ? ms(20) : ms(6), 
      flex: 1, 
      minHeight: ms(36),
      marginVertical: ms(1),
      overflow: 'visible' 
    }} key={item.key}>
      <Pressable
        style={{ 
          minHeight: ms(34), 
          minWidth: '70%',
          borderRadius: ms(6),
          overflow: 'hidden',
          position: 'relative',
          // Add padding to create a buffer zone for hover detection
          padding: ms(2),
          margin: ms(-2), // Negative margin to offset the padding visually
        }}
        onHoverIn={handleHoverIn}
        onHoverOut={handleHoverOut}
        onPress={handlePrimaryPress}
        accessibilityRole="button"
        accessibilityLabel={t(item.header)}
        accessibilityState={{ selected: isCurrentActive }}
      >
        {/* Active indicator line */}
        {isCurrentActive && (
          <View
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: ms(2),
              backgroundColor: theme ? "rgba(90,192,234,1)" : "rgba(71, 152, 187, 1)",
              borderTopRightRadius: ms(1),
              borderBottomRightRadius: ms(1),
              zIndex: 1,
            }}
          />
        )}
        <View style={[
          isHovered ? hoveredStyle : confirmedScreen(item), 
          { 
            flexDirection: 'row', 
            flex: 1, 
            alignSelf: 'stretch', 
            overflow: 'visible', 
            alignItems: 'center',
            paddingHorizontal: ms(6),
            paddingVertical: ms(4),
            paddingLeft: ms(10),
            borderRadius: ms(6),
            ...style, 
            ...webTransition('background-color', '200ms') 
          }
        ]}>
          <Badge color="primary" style={{ top: ms(5) }} badgeContent={badges}>
            {noBackColor ? (
              <View style={{
                padding: ms(8),
                borderRadius: ms(12),
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                top: ms(-5)
              }}>
                {/* NEW feature indicator for noBackColor style */}
                {showNewIndicator && (
                  <Animatable.View
                    animation="pulse"
                    iterationCount="infinite"
                    duration={2000}
                    style={{
                      position: 'absolute',
                      top: ms(-6),
                      right: ms(-6),
                      backgroundColor: item.new ? '#FF4444' : '#FFA500', // Orange for secondary new items
                      borderRadius: ms(10),
                      paddingHorizontal: ms(6),
                      paddingVertical: ms(2),
                      minWidth: ms(20),
                      alignItems: 'center',
                      justifyContent: 'center',
                      shadowColor: item.new ? '#FF4444' : '#FFA500',
                      shadowOffset: { width: 0, height: ms(2) },
                      shadowOpacity: 0.6,
                      shadowRadius: ms(4),
                      elevation: 6,
                      borderWidth: ms(1),
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <Text style={{
                      color: 'white',
                      userSelect: 'none',
                      fontSize: ms(9),
                      fontWeight: 'bold',
                      textAlign: 'center',
                      textShadowColor: 'rgba(0,0,0,0.3)',
                      textShadowOffset: { width: 0, height: ms(1) },
                      textShadowRadius: ms(1),
                    }}>
                      {item.new ? 'NEW' : '🔥'}
                    </Text>
                  </Animatable.View>
                )}
              </View>
            ) : (
              <LinearGradient
                colors={
                  isCurrentActive
                    ? theme 
                      ? ['#667eea', '#764ba2'] 
                      : ['#4a90e2', '#357abd']
                    : isHovered
                    ? theme
                      ? ['rgba(102, 126, 234, 0.8)', 'rgba(118, 75, 162, 0.8)']
                      : ['rgba(74, 144, 226, 0.8)', 'rgba(53, 122, 189, 0.8)']
                    : theme
                    ? ['rgba(102, 126, 234, 0.6)', 'rgba(118, 75, 162, 0.6)']
                    : ['rgba(74, 144, 226, 0.6)', 'rgba(53, 122, 189, 0.6)']
                }
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{
                  padding: ms(8),
                  borderRadius: ms(12),
                  alignItems: 'center',
                  top: ms(-8),
                  justifyContent: 'center',
                  position: 'relative',
                  shadowColor: isCurrentActive 
                    ? (theme ? '#667eea' : '#4a90e2') 
                    : 'transparent',
                  shadowOffset: { width: 0, height: ms(4) },
                  shadowOpacity: isCurrentActive ? 0.3 : 0,
                  shadowRadius: isCurrentActive ? ms(8) : 0,
                  elevation: isCurrentActive ? 6 : 2,
                  ...webTransition('all', '200ms', 'ease-out'),
                  ...(isHovered && {
                    transform: [{ scale: 1.05 }],
                    shadowOpacity: 0.4,
                    shadowRadius: ms(10),
                  }),
                  // Add glow effect for new items (primary or secondary)
                  ...(showNewIndicator && {
                    shadowColor: item.new ? '#FF4444' : '#FFA500',
                    shadowOpacity: 0.3,
                    shadowRadius: ms(6),
                  })
                }}
              >
                {LottieIcon}
                {isCurrentActive && (
                  <View
                    style={{
                      position: 'absolute',
                        top: ms(-2),
                        right: ms(-2),
                        width: ms(6),
                        height: ms(6),
                        borderRadius: ms(3),
                      backgroundColor: theme ? '#4CAF50' : '#00E676',
                      shadowColor: theme ? '#4CAF50' : '#00E676',
                      shadowOffset: { width: 0, height: 0 },
                      shadowOpacity: 0.8,
                        shadowRadius: ms(3),
                      elevation: 3,
                    }}
                  />
                )}
                {/* NEW feature indicator */}
                {showNewIndicator && (
                  <Animatable.View
                    animation="pulse"
                    iterationCount="infinite"
                    duration={2000}
                    style={{
                      position: 'absolute',
                      top: ms(-6),
                      right: ms(-6),
                      backgroundColor: item.new ? '#FF4444' : '#FFA500', // Orange for secondary new items
                      borderRadius: ms(10),
                      paddingHorizontal: ms(6),
                      paddingVertical: ms(2),
                      minWidth: ms(20),
                      alignItems: 'center',
                      justifyContent: 'center',
                      shadowColor: item.new ? '#FF4444' : '#FFA500',
                      shadowOffset: { width: 0, height: ms(2) },
                      shadowOpacity: 0.6,
                      shadowRadius: ms(4),
                      elevation: 6,
                      borderWidth: ms(1),
                      borderColor: 'rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    <Text style={{
                      color: 'white',
                        fontSize: ms(9),
                      fontWeight: 'bold',
                      textAlign: 'center',
                      textShadowColor: 'rgba(0,0,0,0.3)',
                        textShadowOffset: { width: 0, height: ms(1) },
                        textShadowRadius: ms(1),
                    }}>
                        {item.new ? 'NEW' : '🔥'}
                    </Text>
                  </Animatable.View>
                )}
              </LinearGradient>
            )}
          </Badge>
          {menuOpen && (
            <View style={{ 
              flex: 1, 
              justifyContent: 'center', 
              paddingHorizontal: ms(6),
              overflow: 'visible' 
            }}>
              <Text selectable={false} numberOfLines={1} style={[textStyle, { fontSize: ms(13), lineHeight: ms(18) }]}>
                {t(item.header)}
              </Text>
            </View>
          )}
          
          {secondMenuArray?.length > 1 && menuOpen && (
            <View style={{ 
              marginLeft: 'auto',
              justifyContent: 'center', 
              alignItems: 'center', 
              width: ms(28),
              height: ms(28),
              overflow: 'visible' 
            }}>
              <Pressable 
                onPress={(e) => { 
                  setMenuExpand(!menuExpand); 
                }}
                style={{
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: ms(24),
                  height: ms(24),
                  borderRadius: ms(12),
                  backgroundColor: menuExpand 
                    ? theme 
                      ? 'rgba(102, 126, 234, 0.15)' 
                      : 'rgba(74, 144, 226, 0.15)'
                    : 'transparent',
                  ...webTransition('all', '200ms', 'ease-out'),
                  ...(menuExpand && {
                    shadowColor: theme ? '#667eea' : '#4a90e2',
                    shadowOffset: { width: 0, height: ms(2) },
                    shadowOpacity: 0.2,
                    shadowRadius: ms(4),
                    elevation: 2,
                  })
                }}
              >
                <Animatable.View
                  animation={menuExpand ? "rotate" : undefined}
                  duration={200}
                  style={{
                    transform: [{ rotate: menuExpand ? '180deg' : '0deg' }],
                    ...webTransition('transform', '200ms', 'ease-out')
                  }}
                >
                  <MaterialIcons 
                    name="keyboard-arrow-down"
                    size={ms(16)} 
                    color={theme ? "#4a90e2" : "#74B9FF"} 
                    style={{
                      textShadowColor: menuExpand 
                        ? (theme ? 'rgba(74, 144, 226, 0.3)' : 'rgba(116, 185, 255, 0.3)')
                        : 'transparent',
                      textShadowOffset: { width: 0, height: ms(1) },
                      textShadowRadius: ms(2),
                    }}
                  />
                </Animatable.View>
              </Pressable>
            </View>
          )}
        </View>
      </Pressable>
      <View style={secondaryListStyle}>
        {menuExpand && secondMenuArray?.length > 1 && menuOpen && (
          <FlatList
            data={secondMenuArray}
            contentContainerStyle={{
              gap: ms(4),
              marginHorizontal: ms(16),
              paddingVertical: ms(12),
              paddingLeft: ms(24),
              marginBottom: ms(8) 
            }}
            renderItem={({ item: subItem, index }) => {
              // Check both parent stack and child screen for proper highlighting
              // Extract the innermost route name from the page state
              const getCurrentRouteName = () => {
                if (!page.state?.routes) return null;
                const routes = page.state.routes;
                // Get the last route as it's the currently active one in the nested stack
                const currentRoute = routes[routes.length - 1];
                return currentRoute?.name || null;
              };

              const currentInternalRoute = getCurrentRouteName();
              const isParentActive = page.name === (item.stack || item.screen);
              const isSecondaryActive = isParentActive && currentInternalRoute === subItem.screen;

              return (
                <SecondaryMenuItem
                  key={subItem.key}
                  subItem={subItem}
                  index={index}
                  parentScreen={item.screen}
                  isSecondaryActive={isSecondaryActive}
                  theme={theme}
                  _handleNavigate={_handleNavigate}
                />
              );
            }}
            keyExtractor={(subItem, index) => subItem.key + index.toString()}
          />
        )}
      </View>
    </View>
  );
};

export default memo(PrimaryMenu);
